import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import WeatherPulseNav from "./WeatherPulseNav";
import ClusteredMap from "@/components/ClusteredMap";
import { TYPE_COLORS } from "@/pages/EventPage";
import {
  ArrowRight, CloudLightning, Fire, Globe, MapPin, ShieldWarning, Users, Warning, Waves, Wind,
} from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";

const TYPE_ICONS = { cyclone: Wind, flood: Waves, wildfire: Fire, storm: CloudLightning };
const STATUS_DOT = { ACTIVE: "#DC2626", UPDATING: "#D97706", CONFIRMED: "#2563EB", DETECTED: "#D97706" };

function popupHtml(m) {
  return `<div style="font-family:sans-serif;min-width:180px">
    <b>${m.title}</b><br/>
    <span style="color:#6B7280;font-size:11px">${m.event_type} · ${m.status} · severity ${m.severity}/5</span><br/>
    <a href="/events/${m.event_id}" style="color:#0284C7;font-size:12px">Open event record →</a>
  </div>`;
}

/** WeatherPulse Live — the operational dashboard: active alerts, active global
 * events from the Pulse Event Engine, and the world event map. */
export default function WeatherLive() {
  const [events, setEvents] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [now, setNow] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    http.get("/events/active", { params: { limit: 12 } }).then(({ data }) => setEvents(data.events || [])).catch(() => setEvents([]));
    http.get("/events/map", { params: { limit: 600 } })
      .then(({ data }) => setMarkers((data.markers || []).map((m) => ({ ...m, lat: m.location?.lat, lon: m.location?.lon }))))
      .catch(() => {});
    http.get("/weather/alerts/feed", { params: { limit: 8 } }).then(({ data }) => setAlerts(data.alerts || data.items || [])).catch(() => {});
    http.get("/weather-pulse/now").then(({ data }) => setNow(data)).catch(() => {});
    setUpdatedAt(new Date().toISOString().slice(11, 16));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen bg-[#F8FAFC] text-[#0C2D48] pt-32 md:pt-36 pb-24" data-testid="wp-live">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Weather Pulse Live — active alerts and global events, refreshed every minute." testidPrefix="ltbar-wp-live" />
        <div className="mt-4"><PageGuide pageKey="weather:live" testidPrefix="wp-live" /></div>
      </div>
      <SeoMeta
        title="Weather Pulse Live — active global weather & disaster events"
        description="Live global weather: active alerts, tracked disaster events from the Pulse Event Engine, community observations and the world event map — refreshed every minute."
        canonical={`${origin}/weather-pulse/live`}
      />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#0284C7]">Live global weather</div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl tracking-tight" data-testid="wp-live-title">
              The world's weather pulse<span className="text-[#0284C7]">.</span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
              Active alerts and disaster events tracked by the Pulse Event Engine, alongside community
              observations. Not official warnings — in an emergency, follow your national meteorological service.
            </p>
          </div>
          {updatedAt && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FEE2E2] px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#B91C1C]" data-testid="wp-live-indicator">
              <span className="h-1.5 w-1.5 rounded-full bg-[#DC2626] animate-pulse" /> Live · updated {updatedAt} UTC
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wp-live-stats">
          {[
            { label: "Active events", value: events?.length ?? "—", bg: "bg-[#F0F9FF] border border-[#0C2D48]/10", text: "text-[#0C2D48]", accent: "text-[#0369A1]" },
            { label: "Active alerts", value: alerts.length, bg: "bg-[#FFF7ED] border border-[#7C2D12]/10", text: "text-[#7C2D12]", accent: "text-[#C2410C]" },
            { label: "Reports · 24 h", value: now?.total ?? "—", bg: "bg-[#F0FDF4] border border-[#166534]/10", text: "text-[#166534]", accent: "text-[#15803D]" },
            { label: "Countries reporting", value: now?.by_country?.length ?? "—", bg: "bg-[#EFF6FF] border border-[#1E3A8A]/10", text: "text-[#1E3A8A]", accent: "text-[#1D4ED8]" },
          ].map((s) => (
            <div key={s.label} className={`rounded-2xl ${s.bg} p-4`}>
              <div className={`font-serif-display text-3xl ${s.text}`}>{s.value}</div>
              <div className={`font-mono-tech text-[9px] tracking-[0.22em] uppercase ${s.accent}`}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Global map */}
        <section className="mt-8" data-testid="wp-live-map">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-serif-display text-2xl md:text-3xl">Global event map</h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TYPE_COLORS).slice(0, 6).map(([t, c]) => (
                <span key={t} className="inline-flex items-center gap-1 font-mono-tech text-[8px] tracking-[0.14em] uppercase text-[#4B5563]">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c }} /> {t}
                </span>
              ))}
            </div>
          </div>
          <ClusteredMap markers={markers} colorFor={(m) => TYPE_COLORS[m.event_type] || "#4B5563"} popupHtml={popupHtml} testid="wp-live-worldmap" height={460} />
          <p className="mt-2 text-[11px] text-[#6B7280]">
            Disaster alerts via GDACS (UN/OCHA + European Union JRC, CC BY 4.0). Markers show the reported event area.
          </p>
        </section>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {/* Active events */}
          <section data-testid="wp-live-events">
            <h2 className="font-serif-display text-2xl md:text-3xl">Active events</h2>
            <div className="mt-3 space-y-2">
              {events === null ? (
                <div className="text-sm text-[#6B7280]">Loading…</div>
              ) : events.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-black/15 bg-white p-5 text-sm text-[#4B5563]">
                  No high-severity events active right now — a quiet moment on the planet.
                </p>
              ) : (
                events.map((e) => {
                  const Icon = TYPE_ICONS[e.event_type] || Warning;
                  return (
                    <Link key={e.event_id} to={`/events/${e.event_id}`} data-testid={`wp-live-event-${e.event_id}`}
                      className="flex items-start gap-3 rounded-2xl border border-black/5 bg-white p-4 shadow-sm hover:border-[#0284C7]/40 transition-colors">
                      <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: TYPE_COLORS[e.event_type] || "#4B5563" }}>
                        <Icon size={17} weight="fill" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-sm leading-snug">{e.title}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-2 font-mono-tech text-[8px] tracking-[0.16em] uppercase text-[#6B7280]">
                          <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: STATUS_DOT[e.status] || "#6B7280" }} />
                            {e.status}
                          </span>
                          <span>sev {e.severity}/5</span>
                          {e.location?.country && <span>· {e.location.country}</span>}
                        </span>
                      </span>
                      <ArrowRight size={13} weight="bold" className="mt-1 shrink-0 text-[#0284C7]" />
                    </Link>
                  );
                })
              )}
            </div>
          </section>

          {/* Active alerts */}
          <section data-testid="wp-live-alerts">
            <h2 className="font-serif-display text-2xl md:text-3xl">Active alerts</h2>
            <div className="mt-3 space-y-2">
              {alerts.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-black/15 bg-white p-5 text-sm text-[#4B5563]">
                  No active community or official alerts in the network feed right now.
                </p>
              ) : (
                alerts.map((a, i) => (
                  <div key={a.id || i} data-testid={`wp-live-alert-${i}`}
                    className="flex items-start gap-3 rounded-2xl border border-[#B45309]/20 bg-[#FFFBEB] p-4">
                    <ShieldWarning size={18} weight="fill" className="mt-0.5 shrink-0 text-[#B45309]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#78350F] leading-snug">{a.title || a.headline || a.text || "Weather alert"}</div>
                      <div className="mt-1 font-mono-tech text-[8px] tracking-[0.16em] uppercase text-[#92400E]/70">
                        {[a.severity, a.place || a.location?.display_name, a.source].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Community reports mini-strip */}
            {now?.by_condition?.length > 0 && (
              <div className="mt-5 rounded-2xl border border-black/5 bg-white p-4 shadow-sm" data-testid="wp-live-community">
                <div className="flex items-center gap-2 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#6B7280]">
                  <Users size={13} weight="fill" /> Community reports · 24 h
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {now.by_condition.slice(0, 8).map((c) => (
                    <span key={c.condition} className="inline-flex items-center gap-1 rounded-full border border-black/10 px-2.5 py-1 text-xs">
                      {c.emoji} {c.label} <b className="font-mono-tech text-[9px]">{c.count}</b>
                    </span>
                  ))}
                </div>
                <Link to="/weather-pulse/now" className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#0284C7] hover:underline">
                  Full live feed <ArrowRight size={10} weight="bold" />
                </Link>
              </div>
            )}
          </section>
        </div>

        {/* Explore more */}
        <div className="mt-10 flex flex-wrap gap-3" data-testid="wp-live-links">
          {[
            { label: "Report the weather", to: "/weather-pulse#wp-report" },
            { label: "Weather map", to: "/weather-pulse/map" },
            { label: "365-day journal", to: "/weather-pulse/365" },
            { label: "Schools programme", to: "/weather-pulse/schools" },
          ].map((l) => (
            <Link key={l.to} to={l.to} data-testid={`wp-live-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#0284C7]/30 bg-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#0284C7] hover:bg-[#F0F9FF] transition-colors">
              <Globe size={12} weight="bold" /> {l.label}
            </Link>
          ))}
        </div>

        <p className="mt-8 text-[11px] text-[#9CA3AF] flex items-center gap-1.5">
          <MapPin size={11} /> Every event carries a permanent PULSE-EVT ID and an append-only timeline — the record is never overwritten, only extended.
        </p>
      </div>
    </main>
  );
}
